import { TimelineEntry } from '../components/Quote/CorrespondenceTimeline';
import { canResendClientNotice } from './clientNotices';
import { formatQuoteMoney } from './quotePricing';
import { StoredQuote } from './quoteRecord';
import { activityLabel } from './studioDate';

export type JobHistoryKind = 'change' | 'message' | 'money' | 'email';

export interface JobHistoryEntry {
  id: string;
  at: string;
  kind: JobHistoryKind;
  title: string;
  detail: string;
  who: string;
  /** Any stored email, including mail sent to the studio. */
  previewNoticeId?: string;
  /** Client email that can be sent again. Studio-only notices are left out. */
  resendNoticeId?: string;
  noticeType?: string;
  deliveryStatus?: string;
  /** Emails sent for this event. A confirmation lists its email; a message lists the mail that carried it. */
  comms?: JobHistoryComm[];
}

export interface JobHistoryComm {
  noticeId: string;
  label: string;
  detail: string;
  canSend: boolean;
  /** The provider accepted it, or delivery was confirmed. Kept-locally mail is recorded and not sent. */
  sent: boolean;
}

export interface JobHistoryPayment {
  id: string;
  type?: string;
  status?: string;
  verifiedMinor?: number;
  expectedMinor?: number;
  note?: string;
  paidAt?: string | null;
  createdAt?: string;
  reference?: string | null;
  source?: string | null;
}

export interface JobHistoryEnquiry {
  id: string;
  receivedAt: string;
  source?: string;
  shootType?: string;
  notes?: string;
  name?: string;
  linkedQuoteId?: string | null;
}

export interface JobHistoryInput {
  quote?: StoredQuote | null;
  timeline?: TimelineEntry[];
  enquiries?: JobHistoryEnquiry[];
  /** When omitted, completed payments are taken from the conversation timeline. */
  payments?: JobHistoryPayment[] | null;
  arrangement?: { id: string; status?: string; amountMinor?: number; note?: string; createdAt?: string } | null;
  accounting?: Array<{ at: string; by?: string; change?: string; detail?: string }>;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  email: 'Email',
  phoneE164: 'Phone',
  company: 'Company',
  preference: 'Contact preference',
  countryCode: 'Country code',
  message: 'Letter',
};

const NOTICE_TITLES: Record<string, string> = {
  'quotation-issued': 'Email: quotation ready',
  'quotation-follow-up': 'Email: quotation follow-up',
  'booking-secured': 'Email: deposit received',
  'shoot-confirmed': 'Email: shoot confirmed',
  'booking-updated': 'Email: shoot details updated',
  'shoot-reminder': 'Email: shoot reminder',
  'deposit-reminder': 'Email: deposit reminder',
  'balance-requested': 'Email: balance requested',
  'payment-received': 'Email: payment received',
  'paid-in-full': 'Email: paid in full',
  'photos-delivered': 'Email: photographs delivered',
  'client-message': 'Email to the studio',
  'studio-reply': 'Email to the client',
  reminder: 'Email: reply reminder',
  'client-update': 'Email: client changed the quotation',
};

function who(by?: string) {
  const text = String(by || '').trim();
  if (!text || text === 'admin') return 'Carmen';
  if (text === 'client') return 'Client';
  return text;
}

function placeText(value: unknown) {
  if (value == null || value === '') return 'not set';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const place = value as { name?: string; address?: string };
    const name = String(place.name || '').trim();
    const address = String(place.address || '').trim();
    if (name && address && name !== address) return `${name}, ${address}`;
    return name || address || 'not set';
  }
  return String(value);
}

function deliveryLabel(status?: string) {
  if (status === 'captured') return 'Email kept locally. Nothing was sent.';
  if (status === 'not_configured') return 'Email not configured.';
  if (status === 'sent') return 'Accepted by the email provider. Delivery is not confirmed.';
  if (status === 'failed') return 'Email failed.';
  if (status === 'queued') return 'Email queued.';
  if (status === 'preview') return 'Prepared, not sent.';
  if (status === 'delivered') return 'Email delivery confirmed.';
  return status ? `Email status: ${status}.` : '';
}

function activityDetail(entry: NonNullable<StoredQuote['activity']>[number]) {
  const lines: string[] = [];
  if (Array.isArray(entry.changes) && entry.changes.length) lines.push(...entry.changes);
  else if (entry.detail) lines.push(entry.detail);
  if ((entry.from !== undefined || entry.to !== undefined) && !lines.length) {
    lines.push(`${placeText(entry.from)} → ${placeText(entry.to)}`);
  }
  if (Array.isArray(entry.fields) && entry.fields.length && !lines.length) {
    lines.push(entry.fields.map((field) => FIELD_LABELS[field] || field).join(', '));
  }
  if (entry.reason) lines.push(entry.reason);
  return lines.join('\n');
}

function bookingTitle(text: string) {
  if (/complete/i.test(text)) return 'Shoot complete';
  if (/photographs (are ready|have been delivered)/i.test(text)) return 'Photographs delivered';
  if (/proposed date was discarded/i.test(text)) return 'Proposed date discarded';
  if (/new date has been proposed/i.test(text)) return 'Date proposed';
  if (/confirmed/i.test(text) || /confirmation of your booking/i.test(text)) return 'Shoot confirmed';
  if (/private booking notes/i.test(text)) return 'Booking notes';
  return 'Booking updated';
}

function mailWasSent(status?: string) {
  return status === 'sent' || status === 'delivered';
}

/** How many stored emails this shoot has, and how many of those actually went out. */
export function historyMailSummary(entries: JobHistoryEntry[]) {
  const comms = entries.flatMap((entry) => entry.comms || []);
  const recorded = comms.length;
  const sent = comms.filter((comm) => comm.sent).length;
  const noun = recorded === 1 ? 'email' : 'emails';
  let label = 'No emails recorded.';
  if (recorded > 0 && sent === recorded) label = `${recorded} ${noun} sent.`;
  else if (recorded === 1) label = '1 email recorded. Not sent.';
  else if (recorded > 1 && sent === 0) label = `${recorded} emails recorded. None sent.`;
  else if (recorded > 1) label = `${recorded} emails recorded. ${sent} sent.`;
  return { recorded, sent, label };
}

export function itemMailLabel(comms: JobHistoryComm[] = []) {
  const sent = comms.filter((comm) => comm.sent).length;
  const total = comms.length;
  if (!total) return '';
  if (sent === total) return total === 1 ? '1 sent' : `${total} sent`;
  if (sent === 0) return total === 1 ? 'Not sent' : `0 of ${total} sent`;
  return `${sent} of ${total} sent`;
}

function paymentTitle(payment: JobHistoryPayment) {
  if (payment.type === 'refund') return 'Refund recorded';
  if (payment.status === 'complete') return payment.source === 'studio' ? 'Payment recorded' : 'Payment received';
  if (payment.status === 'failed' || payment.status === 'cancelled' || payment.status === 'rejected') return 'Payment did not complete';
  return 'Payment started';
}

function paymentDetail(payment: JobHistoryPayment) {
  const amount = formatQuoteMoney(payment.verifiedMinor || payment.expectedMinor || 0, 'R');
  const lines = [amount];
  if (payment.type && payment.type !== 'payment' && payment.type !== 'refund') lines.push(payment.type);
  if (payment.reference) lines.push(`Reference ${payment.reference}`);
  if (payment.note) lines.push(payment.note);
  if (payment.source === 'studio') lines.push('Entered in the studio.');
  else if (payment.source) lines.push('Card payment.');
  if (payment.status && payment.status !== 'complete') lines.push(payment.status);
  return lines.join('\n');
}

function noticeDetail(entry: TimelineEntry) {
  const delivery = deliveryLabel(entry.deliveryStatus);
  const coveredByMessage = entry.noticeType === 'studio-reply' || entry.noticeType === 'client-message';
  if (coveredByMessage) return delivery || 'Recorded.';
  return [entry.text, delivery].filter(Boolean).join('\n');
}

function timeValue(value?: string) {
  const time = Date.parse(value || '');
  return Number.isNaN(time) ? 0 : time;
}

/**
 * One chronological record of a shoot: quotation changes, booking, messages,
 * payments, emails and account notes. Newest first.
 */
export function buildJobHistory(input: JobHistoryInput): JobHistoryEntry[] {
  const quote = input.quote || null;
  const entries: JobHistoryEntry[] = [];
  const actions = new Set((quote?.activity || []).map((item) => item.action));

  (quote?.activity || []).forEach((item, index) => {
    if (!item || !item.at) return;
    entries.push({
      id: `activity:${item.at}:${item.action}:${index}`,
      at: item.at,
      kind: 'change',
      title: activityLabel(item.action),
      detail: activityDetail(item),
      who: who(item.by),
    });
  });

  if (quote?.createdAt && !actions.has('created') && !actions.has('recorded')) {
    entries.push({
      id: `created:${quote.id || quote.createdAt}`,
      at: quote.createdAt,
      kind: 'change',
      title: 'Quotation started',
      detail: quote.quoteNumber || '',
      who: 'Carmen',
    });
  }

  const paymentsKnown = Array.isArray(input.payments);
  (input.timeline || []).forEach((item) => {
    if (!item || !item.at) return;
    if (item.kind === 'quotation') {
      if (/accepted/i.test(item.text || '') && actions.has('accepted')) return;
      if ((/ready to review/i.test(item.text || '') || /this is your quotation/i.test(item.text || '')) && actions.has('issued')) return;
      entries.push({
        id: item.id,
        at: item.at,
        kind: 'change',
        title: /accepted/i.test(item.text || '') ? 'Quotation accepted' : 'Quotation issued',
        detail: item.text || '',
        who: /accepted/i.test(item.text || '') ? who(item.sender) : 'Carmen',
      });
      return;
    }
    if (item.kind === 'client-message' || item.kind === 'studio-reply' || item.kind === 'internal-note') {
      const whatsApp = item.channel === 'whatsapp-note';
      const title = item.kind === 'client-message'
        ? 'Message from the client'
        : item.kind === 'studio-reply'
          ? 'Message to the client'
          : whatsApp
            ? 'WhatsApp note'
            : 'Private note';
      const mailId = item.mailNoticeId || '';
      entries.push({
        id: item.id,
        at: item.at,
        kind: 'message',
        title,
        detail: item.text || '',
        who: item.kind === 'client-message' ? (item.sender || 'Client') : 'Carmen',
        previewNoticeId: mailId || undefined,
        resendNoticeId: item.kind === 'studio-reply' && mailId ? mailId : undefined,
      });
      return;
    }
    if (item.kind === 'booking') {
      const extra = (item.changes || []).filter((line) => line && line !== item.text);
      entries.push({
        id: item.id,
        at: item.at,
        kind: 'change',
        title: bookingTitle(item.text || ''),
        detail: [item.text, ...extra].filter(Boolean).join('\n'),
        who: who(item.by),
      });
      return;
    }
    if (item.kind === 'payment' || item.kind === 'payment-audit') {
      if (paymentsKnown) return;
      entries.push({
        id: item.id,
        at: item.at,
        kind: 'money',
        title: item.kind === 'payment-audit' ? 'Refund recorded' : 'Payment received',
        detail: [item.text, item.reference ? `Reference ${item.reference}` : ''].filter(Boolean).join('\n'),
        who: 'Carmen',
      });
      return;
    }
    if (item.kind === 'notice') {
      const noticeId = String(item.id || '').startsWith('notice:') ? String(item.id).slice('notice:'.length) : '';
      entries.push({
        id: item.id,
        at: item.at,
        kind: 'email',
        title: NOTICE_TITLES[item.noticeType || ''] || 'Email',
        detail: noticeDetail(item),
        who: 'Carmen',
        noticeType: item.noticeType,
        deliveryStatus: item.deliveryStatus,
        previewNoticeId: noticeId || undefined,
        resendNoticeId: canResendClientNotice(item.noticeType) && noticeId ? noticeId : undefined,
      });
    }
  });

  (input.enquiries || []).forEach((item) => {
    if (!item || !item.receivedAt) return;
    if (item.linkedQuoteId && quote?.id && item.linkedQuoteId !== quote.id) return;
    const lines = [
      item.source ? `Came in via ${item.source}` : '',
      item.shootType || '',
      item.notes || '',
    ].filter(Boolean);
    entries.push({
      id: `enquiry:${item.id}`,
      at: item.receivedAt,
      kind: 'change',
      title: 'Enquiry received',
      detail: lines.join('\n'),
      who: item.name || 'Client',
    });
  });

  if (paymentsKnown) {
    (input.payments || []).forEach((payment) => {
      if (!payment || !payment.id) return;
      entries.push({
        id: `payment:${payment.id}`,
        at: payment.paidAt || payment.createdAt || '',
        kind: 'money',
        title: paymentTitle(payment),
        detail: paymentDetail(payment),
        who: payment.source === 'studio' ? 'Carmen' : 'Client',
      });
    });
  }

  if (input.arrangement && input.arrangement.createdAt) {
    entries.push({
      id: `arrangement:${input.arrangement.id}`,
      at: input.arrangement.createdAt,
      kind: 'money',
      title: 'Payment arrangement',
      detail: [
        input.arrangement.amountMinor ? formatQuoteMoney(input.arrangement.amountMinor, 'R') : '',
        input.arrangement.status || '',
        input.arrangement.note || '',
      ].filter(Boolean).join('\n'),
      who: 'Carmen',
    });
  }

  (input.accounting || []).forEach((item, index) => {
    if (!item || !item.at) return;
    entries.push({
      id: `accounting:${item.at}:${index}`,
      at: item.at,
      kind: 'change',
      title: 'Accounts updated',
      detail: item.detail || 'The accounts record was saved.',
      who: who(item.by),
    });
  });

  const seen = new Set<string>();
  return attachComms(entries)
    .filter((item) => {
      if (!item.at || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    })
    .sort((left, right) => timeValue(right.at) - timeValue(left.at) || left.id.localeCompare(right.id));
}

function commFrom(entry: JobHistoryEntry): JobHistoryComm {
  return {
    noticeId: entry.previewNoticeId || '',
    label: entry.title,
    detail: entry.detail,
    canSend: Boolean(entry.resendNoticeId),
    sent: mailWasSent(entry.deliveryStatus),
  };
}

function titled(rows: JobHistoryEntry[], titles: string[]) {
  return rows.filter((entry) => titles.includes(entry.title));
}

function parentsFor(noticeType: string | undefined, rows: JobHistoryEntry[]) {
  if (noticeType === 'quotation-issued' || noticeType === 'quotation-follow-up') {
    return titled(rows, ['Issued the quotation', 'Quotation issued']);
  }
  if (noticeType === 'shoot-confirmed') return titled(rows, ['Shoot confirmed']);
  if (noticeType === 'booking-updated' || noticeType === 'shoot-reminder') {
    return titled(rows, ['Booking updated', 'Date proposed']);
  }
  if (noticeType === 'booking-secured' || noticeType === 'payment-received' || noticeType === 'paid-in-full' || noticeType === 'deposit-reminder' || noticeType === 'balance-requested') {
    return rows.filter((entry) => entry.kind === 'money');
  }
  if (noticeType === 'photos-delivered') return titled(rows, ['Photographs delivered', 'Shoot complete']);
  return [];
}

function closest(rows: JobHistoryEntry[], at: string) {
  if (!rows.length) return undefined;
  const target = timeValue(at);
  return rows.reduce((best, entry) => (
    Math.abs(timeValue(entry.at) - target) < Math.abs(timeValue(best.at) - target) ? entry : best
  ));
}

/** Put each email on the event it belongs to, and keep a comm line when it stands alone. */
function attachComms(entries: JobHistoryEntry[]) {
  const emails = entries.filter((entry) => entry.kind === 'email' && entry.previewNoticeId);
  const rest = entries.filter((entry) => entry.kind !== 'email');
  const consumed = new Set<string>();
  const add = (entry: JobHistoryEntry, comm: JobHistoryComm) => {
    entry.comms = [...(entry.comms || []), comm];
  };

  rest.forEach((entry) => {
    if (!entry.previewNoticeId) return;
    const notice = emails.find((email) => email.previewNoticeId === entry.previewNoticeId);
    if (!notice || consumed.has(notice.id)) return;
    add(entry, commFrom(notice));
    consumed.add(notice.id);
  });

  emails
    .slice()
    .sort((left, right) => timeValue(left.at) - timeValue(right.at))
    .forEach((notice) => {
      if (consumed.has(notice.id)) return;
      const parent = closest(parentsFor(notice.noticeType, rest), notice.at);
      if (!parent) return;
      add(parent, commFrom(notice));
      consumed.add(notice.id);
    });

  rest.forEach((entry) => {
    if (!entry.previewNoticeId || entry.comms?.some((comm) => comm.noticeId === entry.previewNoticeId)) return;
    add(entry, {
      noticeId: entry.previewNoticeId,
      label: entry.resendNoticeId ? 'Email to the client' : 'Email to the studio',
      detail: '',
      canSend: Boolean(entry.resendNoticeId),
      sent: false,
    });
  });

  const standalone = emails
    .filter((notice) => !consumed.has(notice.id))
    .map((notice) => ({ ...notice, comms: [commFrom(notice)] }));
  return rest.concat(standalone);
}
